import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Page from '../../../common/Page';
import SubHeader from '../../../common/SubHeader';
import MenuCard from '../../../common/MenuCard';
import Loader from '../../../common/Loader';
import EmptyState from '../../../common/EmptyState';
import { listEvents, getTicketCounts } from '../../../common/api';
import AttendeesTotal from '../../../common/AttendeesTotal';
import { useCurrentUser, isSuperAdmin } from '../../../common/UserContext';
import { groupByYearMonth, monthLabel, monthName, formatEventDateTime } from '../../../common/dates';
import { displayEventName, eventCardSubtitle, eventTypeLabel, isPrivateEvent, eventDetailPath, normalizeTypeFilter, filterEventsByType, eventAttendees, attendeesLabel, sumAttendees } from '../../../common/events';
import TypeFilter from '../../../common/TypeFilter';

const BASE_PATH = '/admin/events/past';
const countLabel = (n) => `${n} ${n === 1 ? 'evento' : 'eventi'}`;

// Eventi fino a ieri: anno → mese → lista eventi; il dettaglio si apre in sola lettura
function PastEvents() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const allowed = isSuperAdmin(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [ticketCounts, setTicketCounts] = useState(null); // { eventKey: n } del mese selezionato
  const [countsLoading, setCountsLoading] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    listEvents('past')
      .then((result) => setEvents(result.events || []))
      .catch((err) => {
        console.error('Error fetching events:', err);
        setError('Errore nel caricamento degli eventi');
        toast.error('Errore nel caricamento degli eventi');
      })
      .finally(() => setIsLoading(false));
  }, [allowed]);

  const typeFilter = normalizeTypeFilter(searchParams.get('type'));
  const years = groupByYearMonth(filterEventsByType(events, typeFilter));
  const yearParam = searchParams.get('year');
  const monthParam = searchParams.get('month');
  const year = yearParam ? Number(yearParam) : null;
  const month = monthParam ? Number(monthParam) : null;
  const selectedYear = year ? years.find((y) => y.year === year) : null;
  const selectedMonth = selectedYear && month ? selectedYear.months.find((m) => m.month === month) : null;
  const monthEvents = selectedMonth ? selectedMonth.events : [];

  // Biglietti HubSpot degli eventi pubblici del mese selezionato, con una sola chiamata
  useEffect(() => {
    if (!allowed || !month || monthEvents.length === 0) {
      setTicketCounts(null);
      return;
    }
    const ts = monthEvents.map((e) => e.whenTs);
    const from = Math.min(...ts) - 1;
    const to = Math.max(...ts) + 1;
    let cancelled = false;
    setCountsLoading(true);
    getTicketCounts(from, to)
      .then((result) => { if (!cancelled) setTicketCounts(result.counts || {}); })
      .catch((err) => { console.error('Error fetching ticket counts:', err); if (!cancelled) setTicketCounts({}); })
      .finally(() => { if (!cancelled) setCountsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, year, month, events.length]);

  if (!allowed) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (isLoading) {
    return <Loader />;
  }

  // aggiorna i parametri URL conservando il filtro tipologia
  const setParams = (next) => {
    const params = { ...next };
    if (typeFilter !== 'all') params.type = typeFilter;
    setSearchParams(params);
  };
  const setTypeFilter = (type) => {
    const params = {};
    if (yearParam) params.year = yearParam;
    if (monthParam) params.month = monthParam;
    if (type !== 'all') params.type = type;
    setSearchParams(params);
  };
  const typeSuffix = typeFilter !== 'all' ? `&type=${typeFilter}` : '';

  const openEvent = (event) => {
    const back = `${BASE_PATH}?year=${year}&month=${month}${typeSuffix}`;
    navigate(eventDetailPath(event, { back, readOnly: true }));
  };

  let content;
  if (!year) {
    // Livello 1: anni
    content = (
      <>
        <SubHeader title="Eventi passati" onBack={() => navigate('/admin/dashboard')} />
        <TypeFilter value={typeFilter} onChange={setTypeFilter} />
        {error && <EmptyState text={error} />}
        {!error && years.length === 0 && <EmptyState text="Nessun evento passato" />}
        {years.map((entry) => (
          <MenuCard
            key={entry.year}
            title={String(entry.year)}
            badge={countLabel(entry.count)}
            onClick={() => setParams({ year: String(entry.year) })}
          />
        ))}
      </>
    );
  } else if (!month) {
    // Livello 2: mesi dell'anno
    content = (
      <>
        <SubHeader title={String(year)} onBack={() => setParams({})} />
        <TypeFilter value={typeFilter} onChange={setTypeFilter} />
        {!selectedYear && <EmptyState text="Nessun evento in questo anno" />}
        {selectedYear && selectedYear.months.map((entry) => (
          <MenuCard
            key={entry.month}
            title={monthName(entry.month)}
            badge={countLabel(entry.events.length)}
            onClick={() => setParams({ year: String(year), month: String(entry.month) })}
          />
        ))}
      </>
    );
  } else {
    // Livello 3: eventi del mese
    content = (
      <>
        <SubHeader title={monthLabel(year, month)} onBack={() => setParams({ year: String(year) })} />
        <TypeFilter
          value={typeFilter}
          onChange={setTypeFilter}
          right={selectedMonth && <AttendeesTotal total={sumAttendees(monthEvents, ticketCounts)} loading={countsLoading} />}
        />
        {!selectedMonth && <EmptyState text="Nessun evento in questo mese" />}
        {selectedMonth && selectedMonth.events.map((event) => (
          <MenuCard
            key={event.eventKey}
            title={displayEventName(event.eventName)}
            subtitle={eventCardSubtitle(event, formatEventDateTime(event.when))}
            badge={eventTypeLabel(event)}
            badgeVariant={isPrivateEvent(event) ? 'muted' : 'brand'}
            meta={countsLoading && !isPrivateEvent(event) ? '…' : attendeesLabel(eventAttendees(event, ticketCounts))}
            onClick={() => openEvent(event)}
          />
        ))}
      </>
    );
  }

  return (
    <Page>
      {content}
    </Page>
  );
}

export default PastEvents;
